import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from '../lib/api';

export interface User {
  id: number;
  email: string;
  name?: string;
  role?: string;
  permissions?: string[];
  google_auth_enabled?: boolean;
  // Add other user properties based on your Laravel API response
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (emailOrUsername: string, password: string, recaptchaToken: string) => Promise<{ success: boolean; requires2FA?: boolean; userData?: any }>;
  logout: () => Promise<void>;
  fetchUser: () => Promise<void>;
  fetchPermissions: () => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  setUser: (user: User) => void;
}

export type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (emailOrUsername: string, password: string, recaptchaToken: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authApi.login({
            email: emailOrUsername, // Backend expects 'email' field but accepts both email and username
            password,
            'g-recaptcha-response': recaptchaToken,
          });

          if (!response.success) {
            throw new Error(response.message || 'Login failed');
          }

          // Extract user and token from response
          const { user, token, access_token } = response.data!;
          const authToken = token || access_token;

          // Check if user has 2FA enabled
          if (user.google_auth_enabled) {
            // Store token temporarily for 2FA verification
            set({
              token: authToken,
              isLoading: false,
              error: null,
            });

            return {
              success: true,
              requires2FA: true,
              userData: user,
            };
          }

          // No 2FA required, proceed with normal login
          set({
            user,
            token: authToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // Fetch user permissions after successful login
          try {
            await get().fetchPermissions();
          } catch (permError) {
            console.warn('Failed to fetch permissions:', permError);
          }

          return { success: true };
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'An error occurred during login';
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });
          return { success: false };
        }
      },

      logout: async () => {
        const { token } = get();
        
        // Call API logout if token exists
        if (token) {
          try {
            await authApi.logout(token);
          } catch (error) {
            console.warn('Logout API call failed:', error);
            // Continue with local logout even if API call fails
          }
        }

        // Clear local state
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        });
      },

      fetchUser: async () => {
        const { token } = get();
        if (!token) return;

        try {
          set({ isLoading: true, error: null });
          const response = await authApi.getUser(token);

          if (!response.success) {
            throw new Error(response.message || 'Failed to fetch user data');
          }

          set({
            user: response.data!,
            isLoading: false,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch user data';
          set({
            isLoading: false,
            error: errorMessage,
          });
        }
      },

      fetchPermissions: async () => {
        const { token } = get();
        if (!token) return;

        try {
          const response = await authApi.getPermissions(token);

          if (!response.success) {
            throw new Error(response.message || 'Failed to fetch permissions');
          }

          // Update user with permissions
          const currentUser = get().user;
          if (currentUser) {
            set({
              user: {
                ...currentUser,
                permissions: response.data?.permissions || [],
              },
            });
          }
        } catch (error) {
          console.warn('Failed to fetch permissions:', error);
          // Don't set error state for permissions as it's not critical
        }
      },

      clearError: () => {
        set({ error: null });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setUser: (user: User) => {
        set({ user, isAuthenticated: true });
      },
    }),
    {
      name: 'auth-storage', // Key for localStorage
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
